import type React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface HorizontalBarWidgetProps {
  data: Array<Record<string, unknown>>;
  yKey: string;
  xKey: string;
  color?: string | ((row: Record<string, unknown>) => string);
  height?: number;
  xFormatter?: (value: number) => string;
}

export function HorizontalBarWidget({
  data,
  yKey,
  xKey,
  color = "hsl(210,100%,50%)",
  height = 280,
  xFormatter,
}: HorizontalBarWidgetProps) {
  if (!data || data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-sm text-muted-foreground"
        style={{ height }}
      >
        No data available for selected filters
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        layout="vertical"
        data={data}
        margin={{ top: 8, right: 16, bottom: 8, left: 16 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(210,16%,90%)" />
        <XAxis
          type="number"
          tick={{ fontSize: 11, fill: "hsl(215,16%,40%)" }}
          tickLine={false}
          axisLine={{ stroke: "hsl(210,16%,85%)" }}
          tickFormatter={(v) => (xFormatter ? xFormatter(v) : String(v))}
        />
        <YAxis
          type="category"
          dataKey={yKey}
          tick={{ fontSize: 11, fill: "hsl(215,16%,40%)" }}
          tickLine={false}
          axisLine={{ stroke: "hsl(210,16%,85%)" }}
          width={140}
        />
        <Tooltip
          contentStyle={{
            border: "1px solid hsl(210,16%,85%)",
            borderRadius: 6,
            fontSize: 12,
          }}
          formatter={(value) => {
            if (typeof value === "number" && xFormatter) return xFormatter(value);
            return value as React.ReactNode;
          }}
        />
        <Bar dataKey={xKey} radius={[0, 4, 4, 0]}>
          {data.map((row, idx) => (
            <Cell
              key={idx}
              fill={typeof color === "function" ? color(row) : color}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
